import userflow             from 'userflow.js';

import { Injectable }       from '@angular/core';

import { CompanyService }        from '../companies/company.service';
import { SessionService }        from '../sessions/session.service';
import { UserflowIdentityModel } from './typings/userflow.identity';
import { UserflowSettingsModel } from './typings/userflow.settings';

@Injectable()
export class UserflowService {
  private readonly _defaultLanguage : string = 'en-us';

  constructor(
    private companyService: CompanyService,
    private sessionService: SessionService
  ) {}

  createIdentity ( settings: UserflowSettingsModel ) : UserflowIdentityModel {
    const session = this.sessionService.base();
    const company = this.companyService.company;

    return {
      company_id                      : company.id,
      company_name                    : company.company_name || company.name,
      country_code                    : company.country_code,
      dealer                          : company.dealer,
      demo_user                       : session.demo_user,
      first_login                     : company.first_login,
      has_active_sms_global_account   : company.has_active_sms_global_account,
      has_ashdown_ingram              : company.has_ashdown_ingram,
      has_autoinspect                 : company.has_autoinspect,
      has_carfax                      : company.has_carfax,
      has_carjam                      : company.has_carjam,
      has_checka                      : company.has_checka,
      has_customer_portal             : company.company_settings?.has_customer_portal,
      has_dealership                  : company.has_dealership,
      has_dvla_lookup                 : company.has_dvla_lookup,
      has_enform                      : company.has_enform,
      has_flippay                     : company.has_flippay,
      has_fulcrum                     : company.has_fulcrum,
      has_haynes_pro_lookup           : company.has_haynes_pro_lookup,
      has_hsy                         : company.hsy_settings?.has_hsy,
      has_mailchimp                   : company.has_mailchimp,
      has_myob                        : company.has_myob,
      has_partstech                   : company.partstech_settings?.has_partstech,
      has_prolink                     : company.has_prolink,
      has_quickbooks                  : company.has_quickbooks,
      has_registration_lookup         : company.registration_lookup_settings?.has_registration_lookup,
      has_repco                       : company.has_repco,
      has_sage                        : company.has_sage,
      has_stripe                      : company.stripe_settings?.has_stripe,
      has_supercheap                  : company.has_supercheap,
      has_tillpayments_payment_eft    : company.tillpayments_settings?.has_tillpayments_payment_eft,
      has_tillpayments_payment_gateway: company.tillpayments_settings?.has_tillpayments_payment_gateway,
      has_tiremetrix_registration     : company.tiremetrix_settings?.has_tiremetrix_tire_registration,
      has_tiremetrix_tpms             : company.tiremetrix_settings?.has_tiremetrix_tpms,
      has_tnp                         : company.tnp_settings?.has_tnp,
      has_vehicle_visuals             : company.has_vehicle_visuals,
      has_video_minutes               : company.has_video_minutes,
      has_workshop                    : company.has_workshop,
      has_xero                        : company.has_xero,
      haynes_pro_lookup_requests      : company.haynes_pro_lookup_requests,
      is_admin                        : session.admin,
      is_branch_owner                 : company.is_branch_owner,
      is_capricorn                    : company.is_capricorn,
      is_franchise_owner              : company.is_franchise_owner,
      is_multibranch                  : company.is_multibranch,
      is_region_owner                 : company.is_region_owner,
      limited_active                  : company.limited_active,
      name                            : company.name || session.username,
      needs_myob_setup                : company.needs_myob_setup,
      needs_quickbooks_setup          : company.needs_quickbooks_setup,
      needs_sage_one_aus_setup        : company.needs_sage_one_aus_setup,
      needs_sage_setup                : company.needs_sage_setup,
      needs_xero_setup                : company.needs_xero_setup,
      subscription_plan               : session.subscription_plan,
      subscription_status             : session.subscription_status,
      user_id                         : settings.userId,
    };
  }

  getSettings () : UserflowSettingsModel | null {
    const session  = this.sessionService.base();

    const envKey                 = session?.product_onboarding?.env_key;
    const identityVerificationId = session?.product_onboarding?.identity_verification_id;
    const userId                 = session?.user_id;

    return {
      defaultLanguage: this._defaultLanguage,
      envKey,
      identityVerificationId,
      userId         : userId,
    };
  }

  /**
   * Initializes the SDK and identifies the current user.
   * Gets params from Company and Session services.
   */
  initialize () : void {
    if (!this.isSetup()) {
      console.error('User is not properly setup');

      return;
    }

    const settings = this.getSettings();
    const identity = this.createIdentity(settings);

    userflow.init(settings.envKey);

    userflow.identify(identity.user_id, identity, {
      signature: settings.identityVerificationId
    });
  }

  isSetup () : boolean {
    const company  = this.companyService.company;
    const session  = this.sessionService.base();

    const envKey                 = session?.product_onboarding?.env_key;
    const identityVerificationId = session?.product_onboarding?.identity_verification_id;
    const userId                 = session?.user_id;

    if (!envKey || !identityVerificationId || !userId || !company?.id) {
      return false;
    }

    return true;
  }
}
